import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import config from "../config";

const AuthContext = createContext(null);
const STORAGE_KEY = "smart-solutions-auth";

function getStoredUser() {
  try {
    const localUser =
      window.localStorage.getItem(STORAGE_KEY);

    if (localUser) {
      return {
        user: JSON.parse(localUser),
        remember: true,
      };
    }

    const sessionUser =
      window.sessionStorage.getItem(STORAGE_KEY);

    if (sessionUser) {
      return {
        user: JSON.parse(sessionUser),
        remember: false,
      };
    }
  } catch (error) {
    console.error(
      "Unable to read stored authentication:",
      error
    );
  }

  return {
    user: null,
    remember: false,
  };
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);
  const [authError, setAuthError] = useState("");

  const clearStoredUser = useCallback(() => {
    window.localStorage.removeItem(STORAGE_KEY);
    window.sessionStorage.removeItem(STORAGE_KEY);
  }, []);

  const saveUserToStorage = useCallback(
    (userData, remember = null) => {
      let shouldRemember = remember;

      if (shouldRemember === null) {
        shouldRemember = Boolean(
          window.localStorage.getItem(STORAGE_KEY)
        );
      }

      if (shouldRemember) {
        window.localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify(userData)
        );

        window.sessionStorage.removeItem(STORAGE_KEY);
      } else {
        window.sessionStorage.setItem(
          STORAGE_KEY,
          JSON.stringify(userData)
        );

        window.localStorage.removeItem(STORAGE_KEY);
      }
    },
    []
  );

  const checkSession = useCallback(
    async (storedUser = null) => {
      setAuthError("");

      try {
        const response = await fetch(
          `${config.API_URL}/session.php?t=${Date.now()}`,
          {
            method: "GET",
            credentials: "include",
            cache: "no-store",
            headers: {
              Accept: "application/json",
              "Cache-Control": "no-cache",
              Pragma: "no-cache",
            },
          }
        );

        const responseText = await response.text();

        let result;

        try {
          result = JSON.parse(responseText);
        } catch {
          console.error(
            "Invalid session.php response:",
            responseText
          );

          throw new Error(
            "Session API returned an invalid response."
          );
        }

        if (
          !response.ok ||
          !result.success ||
          result.authenticated === false
        ) {
          setUser(null);
          clearStoredUser();

          return null;
        }

        const sessionUser =
          result.user &&
          typeof result.user === "object"
            ? result.user
            : result;

        const restoredUser = {
          UserID:
            sessionUser.UserID ??
            sessionUser.mobile ??
            "",

          UserName:
            sessionUser.UserName ??
            sessionUser.name ??
            "",

          cspid:
            sessionUser.cspid ??
            "",

          status:
            sessionUser.status ??
            "",

          walletBalance:
            Number(
              sessionUser.walletBalance ??
              storedUser?.walletBalance ??
              0
            ) || 0,

          loggedInAt:
            sessionUser.loggedInAt ??
            storedUser?.loggedInAt ??
            new Date().toISOString(),
        };

        /*
         * Do not authenticate using browser storage alone.
         * PHP session must return a valid UserID.
         */
        if (!restoredUser.UserID) {
          setUser(null);
          clearStoredUser();

          return null;
        }

        const remember = Boolean(
          window.localStorage.getItem(STORAGE_KEY)
        );

        setUser(restoredUser);
        saveUserToStorage(restoredUser, remember);

        return restoredUser;
      } catch (error) {
        console.error(
          "Session check failed:",
          error
        );

        setAuthError(
          error.message ||
          "Unable to verify login session."
        );

        /*
         * For security, do not keep the user logged in
         * when the PHP session cannot be verified.
         */
        setUser(null);
        clearStoredUser();

        return null;
      }
    },
    [clearStoredUser, saveUserToStorage]
  );

  useEffect(() => {
    let active = true;

    const restoreAuthentication = async () => {
      setReady(false);

      const stored = getStoredUser();

      await checkSession(stored.user);

      if (active) {
        setReady(true);
      }
    };

    restoreAuthentication();

    return () => {
      active = false;
    };
  }, [checkSession]);

  const login = useCallback(
    (userData, remember = true) => {
      const nextUser = {
        UserID:
          userData.UserID ??
          userData.mobile ??
          "",

        UserName:
          userData.UserName ??
          userData.name ??
          "",

        cspid:
          userData.cspid ??
          "",

        status:
          userData.status ??
          "",

        walletBalance:
          Number(userData.walletBalance) || 0,

        loggedInAt:
          userData.loggedInAt ??
          new Date().toISOString(),
      };

      setUser(nextUser);
      setAuthError("");
      setReady(true);

      saveUserToStorage(nextUser, remember);

      return nextUser;
    },
    [saveUserToStorage]
  );

  const getWalletBalance = useCallback(async () => {
    try {
      const response = await fetch(
        `${config.API_URL}/wallet.php?t=${Date.now()}`,
        {
          method: "GET",
          credentials: "include",
          cache: "no-store",
          headers: {
            Accept: "application/json",
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
          },
        }
      );

      const responseText = await response.text();

      let result;

      try {
        result = JSON.parse(responseText);
      } catch {
        console.error(
          "Invalid wallet.php response:",
          responseText
        );

        throw new Error(
          "Wallet API returned an invalid response."
        );
      }

      if (!response.ok || !result.success) {
        if (response.status === 401) {
          setUser(null);
          clearStoredUser();
        }

        throw new Error(
          result.message ||
          "Unable to load wallet balance."
        );
      }

      const updatedBalance =
        Number(result.walletBalance) || 0;

      setUser((previousUser) => {
        if (!previousUser) {
          return null;
        }

        const updatedUser = {
          ...previousUser,
          walletBalance: updatedBalance,
        };

        saveUserToStorage(updatedUser);

        return updatedUser;
      });

      return updatedBalance;
    } catch (error) {
      console.error(
        "Wallet API error:",
        error
      );

      return null;
    }
  }, [
    clearStoredUser,
    saveUserToStorage,
  ]);

  const logout = useCallback(async () => {
    /*
     * Clear React and browser authentication immediately.
     */
    setUser(null);
    clearStoredUser();
    setAuthError("");
    setReady(true);

    try {
      const response = await fetch(
        `${config.API_URL}/logout.php?t=${Date.now()}`,
        {
          method: "POST",
          credentials: "include",
          cache: "no-store",
          headers: {
            Accept: "application/json",
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
          },
        }
      );

      const responseText = await response.text();

      if (responseText) {
        try {
          const result = JSON.parse(responseText);

          if (!response.ok || !result.success) {
            throw new Error(
              result.message ||
              "Logout request failed."
            );
          }
        } catch (error) {
          if (error instanceof SyntaxError) {
            console.error(
              "Invalid logout.php response:",
              responseText
            );

            throw new Error(
              "Logout API returned an invalid response."
            );
          }

          throw error;
        }
      } else if (!response.ok) {
        throw new Error(
          `Logout failed with status ${response.status}.`
        );
      }

      return true;
    } catch (error) {
      console.error(
        "Logout API error:",
        error
      );

      /*
       * The browser remains logged out even if the API fails.
       */
      return false;
    }
  }, [clearStoredUser]);

  const contextValue = useMemo(
    () => ({
      user,
      login,
      logout,
      ready,
      authError,
      checkSession,
      getWalletBalance,
      isAuthenticated: Boolean(user),
    }),
    [
      user,
      login,
      logout,
      ready,
      authError,
      checkSession,
      getWalletBalance,
    ]
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error(
      "useAuth must be used within AuthProvider."
    );
  }

  return context;
};

export default AuthContext;

